import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ChevronLeft, Pencil, FileText, Phone, Mail, User, Baby, AlertTriangle, Download, ChevronDown, Sparkles, X, Loader, CheckCircle2, FileSignature, CalendarCheck, Eye, Archive, ArchiveRestore, Trash2, ClipboardList } from 'lucide-react';
import { enfantsApi, piecesJointesApi, mailsApi, devisApi } from '../../utils/api';
import SignatureButton from '../../components/Signature/SignatureButton';
import EnvoiDocumentButton from '../../components/EnvoiDocumentButton';
import { getStatutBadgeClass, getStatutLabel, formatMontant, getHeuresLissees } from '../../utils/calculs';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import SuiviDeveloppement from './SuiviDeveloppement';
import DocumentsAutorisations from './DocumentsAutorisations';
import PiecesJointes from '../../components/PiecesJointes';
import { useAuth } from '../../context/AuthContext';
import { estCompteCreche } from '../../utils/permissions';
import { openPdf } from '../../components/PdfPreview';
import MandatSepaCard from './MandatSepaCard';
import AvenantSuivi from './AvenantSuivi';
import ContratAccueilCard from './ContratAccueilCard';

// Encadré repliable/dépliable réutilisable (accordéon).
function Collapsible({ title, icon: Icon, defaultOpen = false, badge, flatten = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="card overflow-hidden">
      <button type="button" onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between gap-2 px-5 py-4 text-left hover:bg-gray-50/60 transition-colors">
        <span className="flex items-center gap-2 font-semibold text-gray-700">
          {Icon && <Icon size={17} className="text-primary-500" />} {title}
        </span>
        <span className="flex items-center gap-2">{badge}
          <ChevronDown size={18} className={`text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
        </span>
      </button>
      {open && (
        <div className={`border-t border-gray-100 ${flatten ? 'p-0 [&>.card]:shadow-none [&>.card]:border-0 [&>.card]:rounded-none' : 'px-5 pb-5 pt-4'}`}>
          {children}
        </div>
      )}
    </div>
  );
}

// Planning de l'enfant : planning CONTRACTUEL modifiable (jours + horaires + volume),
// alerte avenant, cohérence avec le quotidien, + exceptions ponctuelles.
const JOURS_SEM = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi'];
function dureeHeures(d, f) {
  if (!d || !f) return 0;
  const [h1, m1] = d.split(':').map(Number), [h2, m2] = f.split(':').map(Number);
  if ([h1, m1, h2, m2].some(isNaN)) return 0;
  return Math.max(0, (h2 * 60 + m2 - h1 * 60 - m1) / 60);
}
const fmtH = (h) => h.toLocaleString('fr-FR', { maximumFractionDigits: 2 });
function PlanningCard({ enfantId, emailFamille, nomFamille, readOnly = false }) {
  const [avKey, setAvKey] = useState(0); // recharge le rappel avenant après enregistrement
  const [data, setData] = useState(null);
  const [grid, setGrid] = useState({});
  const [savingC, setSavingC] = useState(false);
  const [cMsg, setCMsg] = useState(null);
  const [date, setDate] = useState('');
  const [type, setType] = useState('absent');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const toGrid = (contrat) => {
    const g = {};
    JOURS_SEM.forEach(j => { g[j] = { on: false, debut: '08:30', fin: '17:30' }; });
    (contrat?.jours || []).forEach(r => { g[r.jour] = { on: true, debut: r.heure_debut || '08:30', fin: r.heure_fin || '17:30' }; });
    return g;
  };

  const load = React.useCallback(() => {
    enfantsApi.getPlanning(enfantId)
      .then(r => { setData(r.data); setGrid(toGrid(r.data.contrat)); })
      .catch(() => setData({ contrat: { jours: [], heures_hebdo: 0 }, exceptions: [], coherence: { ok: true, ecarts: [] } }));
  }, [enfantId]);
  useEffect(() => { load(); }, [load]);

  const setDay = (j, patch) => setGrid(g => ({ ...g, [j]: { ...g[j], ...patch } }));
  const heuresTotal = Math.round(JOURS_SEM.reduce((t, j) => t + (grid[j]?.on ? dureeHeures(grid[j].debut, grid[j].fin) : 0), 0) * 100) / 100;
  // Toute heure commencée est due : le volume hebdomadaire s'arrondit à l'heure supérieure (34,5 h → 35 h)
  const heuresDues = Math.ceil(heuresTotal - 1e-9);

  const enregistrerContrat = async () => {
    setSavingC(true); setCMsg(null);
    try {
      const jours = JOURS_SEM.filter(j => grid[j]?.on).map(j => ({ jour: j, heure_debut: grid[j].debut, heure_fin: grid[j].fin }));
      const r = await enfantsApi.setContratPlanning(enfantId, jours);
      setCMsg({ type: r.data.avenant ? 'avenant' : 'ok', text: r.data.message });
      setAvKey(k => k + 1);
      load();
    } catch (e) { setCMsg({ type: 'error', text: e.response?.data?.error || 'Erreur' }); }
    setSavingC(false);
  };

  const ajouter = async () => {
    if (!date) { setMsg({ type: 'error', text: 'Choisissez une date.' }); return; }
    setBusy(true); setMsg(null);
    try {
      const r = await enfantsApi.addPlanningException(enfantId, { date, type, note });
      setMsg({ type: 'ok', text: r.data.message }); setDate(''); setNote(''); load();
    } catch (e) { setMsg({ type: 'error', text: e.response?.data?.error || 'Erreur' }); }
    setBusy(false);
  };
  const supprimer = async (excId) => {
    try { await enfantsApi.deletePlanningException(enfantId, excId); load(); } catch { /* noop */ }
  };

  const fmt = (d) => { try { return format(parseISO(d), 'EEEE d MMMM yyyy', { locale: fr }); } catch { return d; } };
  const cap = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1) : s;

  return (
    <div className="card p-4">
      {readOnly ? (
        <p className="text-xs text-gray-400 mb-3">Jours et horaires habituels de l'enfant, et ajustements ponctuels (absences / jours en plus). Consultation seule.</p>
      ) : (
      <p className="text-xs text-gray-400 mb-3">Planning <strong>contractuel</strong> (jours + horaires) modifiable. Toute modification génère une alerte « avenant à faire signer » ; si le volume horaire change, les semaines à venir s'adaptent. Les ajustements ponctuels (absences / jours en plus) sont pris en compte dans l'attestation.</p>
      )}

      {!data ? <p className="text-sm text-gray-400">Chargement…</p> : (
        <>
          {/* Alerte cohérence quotidien vs contrat */}
          {data.coherence && !data.coherence.ok && (
            <div className="mb-3 text-[12.5px] bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-3 py-2">
              <div className="font-semibold flex items-center gap-1.5"><AlertTriangle size={14} /> Le planning quotidien ne correspond pas au contrat</div>
              <ul className="list-disc ml-5 mt-1">{data.coherence.ecarts.map((e, i) => <li key={i}>{e}</li>)}</ul>
              {data.coherence.heures_reel != null && <div className="mt-1">Volume : {data.coherence.heures_reel} h (quotidien) vs {data.coherence.heures_contrat} h (contrat).</div>}
            </div>
          )}

          {/* Planning contractuel éditable */}
          <div className="rounded-xl border border-gray-100 p-3 mb-3">
            <div className="flex items-start justify-between mb-2">
              <span className="text-[13px] font-medium text-gray-600">{readOnly ? 'Planning habituel' : 'Planning contractuel'}</span>
              <div className="text-right">
                <div className="text-[12.5px] font-semibold text-primary-600">
                  {heuresDues} h / semaine
                  {heuresDues !== heuresTotal && <span className="font-normal text-gray-400"> ({fmtH(heuresTotal)} h planifiées)</span>}
                </div>
                <div className="text-[11.5px] text-gray-500" title="Heures hebdomadaires (arrondies à l'heure supérieure) × 47 semaines ÷ 12 mois">
                  {fmtH(getHeuresLissees(heuresDues))} h / mois (lissé 47 sem. / 52)
                </div>
              </div>
            </div>
            <div className="space-y-1.5">
              {JOURS_SEM.map(j => (
                <div key={j} className="flex items-center gap-2 text-sm">
                  <label className="flex items-center gap-1.5 w-28">
                    <input type="checkbox" checked={grid[j]?.on || false} disabled={readOnly} onChange={e => setDay(j, { on: e.target.checked })} />
                    <span className={grid[j]?.on ? 'text-gray-800' : 'text-gray-400'}>{cap(j)}</span>
                  </label>
                  <input type="time" value={grid[j]?.debut || ''} disabled={readOnly || !grid[j]?.on} onChange={e => setDay(j, { debut: e.target.value })} className="form-input text-sm py-1 w-28 disabled:opacity-40" />
                  <span className="text-gray-400">→</span>
                  <input type="time" value={grid[j]?.fin || ''} disabled={readOnly || !grid[j]?.on} onChange={e => setDay(j, { fin: e.target.value })} className="form-input text-sm py-1 w-28 disabled:opacity-40" />
                  {grid[j]?.on && <span className="text-[11.5px] text-gray-400">{Math.round(dureeHeures(grid[j].debut, grid[j].fin) * 10) / 10} h</span>}
                </div>
              ))}
            </div>
            {!readOnly && (<>
            <div className="flex items-center gap-2 mt-2">
              <button onClick={enregistrerContrat} disabled={savingC} className="btn-primary py-1.5">{savingC ? <Loader size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} Enregistrer le contrat</button>
              {data.contrat?.source === 'devis' && <span className="text-[11px] text-gray-400">(initialisé depuis le devis)</span>}
            </div>
            {cMsg && (
              <div className={`mt-2 text-[12.5px] rounded px-3 py-2 ${cMsg.type === 'error' ? 'bg-red-50 text-red-600' : cMsg.type === 'avenant' ? 'bg-amber-50 text-amber-800 border border-amber-200' : 'bg-emerald-50 text-emerald-600'}`}>
                {cMsg.type === 'avenant' && <span className="font-semibold">⚠️ </span>}{cMsg.text}
                {cMsg.type === 'avenant' && <span> Une échéance « avenant » a été créée.</span>}
              </div>
            )}
            <div className="mt-3">
              <AvenantSuivi enfantId={enfantId} refreshKey={avKey} emailFamille={emailFamille} nomFamille={nomFamille} />
            </div>
            </>)}
          </div>

          {!readOnly && (
          <div className="rounded-xl bg-gray-50 border border-gray-100 p-3">
            <div className="text-[13px] font-medium text-gray-600 mb-2">Ajouter un ajustement</div>
            <div className="flex flex-wrap items-end gap-2">
              <div>
                <label className="block text-[11px] text-gray-500 mb-0.5">Date</label>
                <input type="date" value={date} onChange={e => setDate(e.target.value)} className="form-input text-sm py-1.5" />
              </div>
              <div>
                <label className="block text-[11px] text-gray-500 mb-0.5">Type</label>
                <select value={type} onChange={e => setType(e.target.value)} className="form-select text-sm py-1.5 w-44">
                  <option value="absent">Absent ce jour</option>
                  <option value="present">Présent en plus</option>
                </select>
              </div>
              <div className="flex-1 min-w-[140px]">
                <label className="block text-[11px] text-gray-500 mb-0.5">Note (facultatif)</label>
                <input value={note} onChange={e => setNote(e.target.value)} placeholder="ex. demande des parents" className="form-input text-sm py-1.5 w-full" />
              </div>
              <button onClick={ajouter} disabled={busy} className="btn-primary py-1.5">{busy ? <Loader size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} Ajouter</button>
            </div>
            {msg && <div className={`mt-2 text-[12.5px] rounded px-2 py-1 ${msg.type === 'error' ? 'bg-red-50 text-red-600' : 'bg-emerald-50 text-emerald-600'}`}>{msg.text}</div>}
          </div>
          )}

          {data.exceptions?.length > 0 && (
            <div className="mt-3 divide-y divide-gray-50 border border-gray-100 rounded-xl">
              {data.exceptions.map(ex => (
                <div key={ex.id} className="flex items-center gap-2 px-3 py-2 text-sm">
                  <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${ex.type === 'absent' ? 'bg-red-50 text-red-600' : 'bg-emerald-50 text-emerald-600'}`}>
                    {ex.type === 'absent' ? 'Absent' : 'Présent +'}
                  </span>
                  <span className="text-gray-700">{cap(fmt(ex.date))}</span>
                  {ex.note && <span className="text-gray-400 text-[12.5px] truncate">— {ex.note}</span>}
                  {!readOnly && <button onClick={() => supprimer(ex.id)} className="ml-auto text-gray-300 hover:text-red-500" title="Supprimer"><X size={15} /></button>}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// Génération de l'attestation de présence mensuelle (dépôt dans la bibliothèque).
function AttestationCard({ enfantId, onDeposee, destinataireEmail }) {
  const moisOptions = React.useMemo(() => {
    const opts = [];
    const now = new Date();
    for (let i = 1; i <= 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const label = d.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
      opts.push({ val, label: label.charAt(0).toUpperCase() + label.slice(1) });
    }
    return opts;
  }, []);
  const [mois, setMois] = useState(moisOptions[0].val);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const generer = async () => {
    setBusy(true); setMsg(null);
    try {
      const { data } = await enfantsApi.genererAttestation(enfantId, mois);
      setMsg({ type: 'ok', text: data.message });
      onDeposee && onDeposee();
    } catch (e) { setMsg({ type: 'error', text: e.response?.data?.error || 'Génération impossible.' }); }
    setBusy(false);
  };

  return (
    <div className="card p-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs text-gray-400">Liste les jours de présence du mois (d'après le planning de l'enfant) et se dépose dans la bibliothèque. Générée aussi automatiquement au 1er de chaque mois.</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={mois} onChange={e => setMois(e.target.value)} className="form-select text-sm w-44">
            {moisOptions.map(o => <option key={o.val} value={o.val}>{o.label}</option>)}
          </select>
          <a href={enfantsApi.getAttestationPdfUrl(enfantId, mois)} target="_blank" rel="noopener noreferrer" className="btn-secondary" title="Aperçu">
            <Eye size={15} /> Aperçu
          </a>
          <button onClick={generer} disabled={busy} className="btn-primary">
            {busy ? <Loader size={15} className="animate-spin" /> : <CalendarCheck size={15} />} Générer et déposer
          </button>
          <EnvoiDocumentButton type="attestation" enfantId={enfantId} mois={mois} destinataireDefaut={destinataireEmail} label="Envoyer" />
        </div>
      </div>
      {msg && <div className={`mt-3 text-[13px] rounded-lg px-3 py-2 ${msg.type === 'error' ? 'bg-red-50 text-red-600' : 'bg-emerald-50 text-emerald-600'}`}>{msg.text}</div>}
    </div>
  );
}

// Suivi des e-mails échangés avec la famille (boîte contact@ reliée en IMAP).
// Réservé à la gestion ; l'appelant ne rend ce bloc que pour admin/direction.
function SuiviMails({ enfantId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [open, setOpen] = useState(null);
  const [msg, setMsg] = useState('');

  const load = React.useCallback(() => {
    setLoading(true);
    enfantsApi.getEmails(enfantId)
      .then(r => setData(r.data))
      .catch(() => setData({ configured: false, mails: [] }))
      .finally(() => setLoading(false));
  }, [enfantId]);
  useEffect(() => { load(); }, [load]);

  const sync = async () => {
    setSyncing(true); setMsg('');
    try {
      const r = await mailsApi.sync();
      setMsg(`Relevé effectué (+${r.data.ajoutes || 0} e-mail(s)).`);
      load();
    } catch (e) {
      setMsg(e.response?.data?.error || 'Relevé impossible.');
    }
    setSyncing(false);
    setTimeout(() => setMsg(''), 5000);
  };

  const fmt = (iso) => { try { return format(parseISO(iso), "d MMM yyyy 'à' HH'h'mm", { locale: fr }); } catch { return iso || ''; } };
  const mails = data?.mails || [];

  return (
    <div className="card p-5">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <span className="text-xs text-gray-400">{mails.length > 0 ? `${mails.length} e-mail(s)` : 'Aucun e-mail'}</span>
        <div className="flex items-center gap-2">
          {msg && <span className="text-xs text-gray-500">{msg}</span>}
          {data?.configured && (
            <button onClick={sync} disabled={syncing}
              className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50">
              {syncing ? <Loader size={13} className="animate-spin" /> : <Download size={13} />} Actualiser
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-gray-400 py-3">Chargement…</p>
      ) : !data?.configured ? (
        <p className="text-[13px] text-gray-500 bg-gray-50 border border-gray-100 rounded-lg px-3 py-2.5">
          La boîte <strong>contact@lescachettes.fr</strong> n'est pas encore reliée. Une fois connectée, les e-mails échangés avec cette famille s'afficheront ici automatiquement.
        </p>
      ) : (data?.emails_famille || []).length === 0 ? (
        <p className="text-[13px] text-gray-500">Aucune adresse e-mail renseignée pour les parents — ajoutez-la dans la fiche pour rattacher les e-mails.</p>
      ) : mails.length === 0 ? (
        <p className="text-[13px] text-gray-500">Aucun e-mail trouvé avec cette famille pour le moment.</p>
      ) : (
        <div className="divide-y divide-gray-100 border border-gray-100 rounded-xl overflow-hidden">
          {mails.map(m => {
            const isOpen = open === m.id;
            const recu = m.direction === 'recu';
            return (
              <div key={m.id} className={isOpen ? 'bg-gray-50/60' : ''}>
                <button onClick={() => setOpen(isOpen ? null : m.id)} className="w-full text-left px-3 py-2.5 flex items-start gap-2.5 hover:bg-gray-50">
                  <span className={`mt-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full flex-shrink-0 ${recu ? 'bg-emerald-50 text-emerald-600' : 'bg-blue-50 text-blue-600'}`}>
                    {recu ? 'Reçu' : 'Envoyé'}
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-[13.5px] font-medium text-gray-800 truncate">{m.sujet || '(sans objet)'}</span>
                    <span className="block text-[11.5px] text-gray-400 truncate">
                      {recu ? `De ${m.de_email}` : `À ${m.a_email}`} · {fmt(m.date_mail)}
                    </span>
                  </span>
                  <ChevronDown size={15} className={`text-gray-300 flex-shrink-0 mt-1 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                </button>
                {isOpen && (
                  <div className="px-3 pb-3 -mt-0.5">
                    <div className="text-[11px] text-gray-400 mb-1.5">
                      De : {m.de_email} — À : {m.a_email}
                    </div>
                    <div className="text-[12.5px] text-gray-700 whitespace-pre-wrap leading-relaxed bg-white border border-gray-100 rounded-lg p-3 max-h-72 overflow-auto">
                      {m.extrait || <span className="italic text-gray-300">(aperçu indisponible)</span>}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Menu d'export du dossier, options filtrées selon le rôle
function ExportDossier({ enfantId }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const role = user?.role;
  const options = [
    { label: 'Dossier seul', include: [] },
    ...(role !== 'equipe' ? [{ label: 'Dossier + contrat', include: ['contrat'] }] : []),
    ...(role === 'admin' ? [
      { label: 'Dossier + factures', include: ['factures'] },
      { label: 'Dossier + contrat + factures', include: ['contrat', 'factures'] },
    ] : []),
  ];
  const exporter = (include) => {
    openPdf(enfantsApi.getDossierPdfUrl(enfantId, include));
    setOpen(false);
  };
  return (
    <div className="relative">
      <button onClick={() => setOpen(o => !o)} className="btn-secondary">
        <Download size={15} /> Exporter le dossier <ChevronDown size={14} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-1 z-20 bg-white rounded-xl shadow-xl border border-gray-100 py-1 w-60">
            {options.map(o => (
              <button key={o.label} onClick={() => exporter(o.include)}
                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2">
                <FileText size={14} className="text-gray-400" /> {o.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function InfoRow({ label, value }) {
  if (!value) return null;
  return (
    <div className="flex gap-2 text-sm">
      <span className="text-gray-500 w-36 flex-shrink-0">{label}</span>
      <span className="text-gray-800 font-medium">{value}</span>
    </div>
  );
}

function ExtractModal({ extract, applying, onCancel, onApply }) {
  const e = extract.enfant || {}, m = extract.medecin || {}, p1 = extract.parent1 || {}, p2 = extract.parent2 || {}, c = extract.contrat || {};
  const groups = [
    ['Enfant', [['Prénom', e.prenom], ['Nom', e.nom], ['Date de naissance', e.date_naissance], ['Sexe', e.sexe === 'M' ? 'Masculin' : e.sexe === 'F' ? 'Féminin' : e.sexe], ['Adresse', e.adresse]]],
    ['Médecin', [['Nom', m.nom], ['Téléphone', m.tel]]],
    ['Parent 1', [['Prénom', p1.prenom], ['Nom', p1.nom], ['Lien', p1.lien], ['Téléphone', p1.telephone_portable], ['Email', p1.email]]],
    ['Parent 2', [['Prénom', p2.prenom], ['Nom', p2.nom], ['Lien', p2.lien], ['Téléphone', p2.telephone_portable], ['Email', p2.email]]],
    ['Contrat', [['Début', c.date_debut], ['Fin', c.date_fin]]],
  ].map(([t, r]) => [t, r.filter(([, v]) => v != null && v !== '')]).filter(([, r]) => r.length);

  return (
    <div className="fixed inset-0 bg-navy-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onCancel}>
      <div className="bg-white rounded-[22px] shadow-2xl w-full max-w-lg max-h-[85vh] overflow-hidden flex flex-col" onClick={e2 => e2.stopPropagation()}>
        <div className="flex items-center justify-between px-6 pt-5 pb-2">
          <h3 className="font-serif text-[20px] text-navy-900 flex items-center gap-2"><Sparkles size={18} className="text-gold-600" /> Infos extraites du document</h3>
          <button onClick={onCancel} className="w-8 h-8 rounded-full bg-navy-50 hover:bg-navy-100 flex items-center justify-center text-navy-400"><X size={17} /></button>
        </div>
        <p className="px-6 text-[12.5px] text-navy-400 mb-2">Depuis « {extract.source} ». Vérifiez puis appliquez : un champ déjà rempli n'est remplacé que si l'IA a trouvé une valeur.</p>
        <div className="px-6 pb-2 overflow-y-auto space-y-4">
          {groups.length === 0 ? (
            <p className="text-[13px] text-navy-400 py-6 text-center">Aucune information exploitable trouvée dans ce document.</p>
          ) : groups.map(([titre, r]) => (
            <div key={titre}>
              <div className="text-[11px] font-semibold uppercase tracking-wide text-gold-700 mb-1">{titre}</div>
              <div className="rounded-xl border border-cream-300/70 divide-y divide-cream-200">
                {r.map(([label, val]) => (
                  <div key={label} className="flex items-center justify-between px-3 py-1.5 text-[13.5px] gap-4">
                    <span className="text-navy-400 flex-shrink-0">{label}</span><span className="text-navy-800 font-medium text-right">{val}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="flex justify-end gap-2 px-6 py-4 border-t border-cream-200">
          <button onClick={onCancel} className="apple-btn-secondary">Annuler</button>
          <button onClick={onApply} disabled={applying || groups.length === 0} className="apple-btn-primary">
            {applying ? <><Loader size={15} className="animate-spin" /> Application…</> : <><CheckCircle2 size={15} /> Appliquer au dossier</>}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function EnfantDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [extract, setExtract] = useState(null); // { source, enfant, medecin, parent1, parent2, contrat }
  const [extractErr, setExtractErr] = useState(null);
  const [applying, setApplying] = useState(false);
  const [biblioKey, setBiblioKey] = useState(0); // force le rechargement de la bibliothèque
  const [delOpen, setDelOpen] = useState(false);
  const [delText, setDelText] = useState('');
  const [deleting, setDeleting] = useState(false);

  const reload = () => enfantsApi.getById(id).then(res => setData(res.data)).catch(() => {});

  useEffect(() => {
    enfantsApi.getById(id)
      .then(res => setData(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [id]);

  const handleExtract = async (pj) => {
    setExtractErr(null);
    try {
      const res = await piecesJointesApi.extraire(pj.id);
      setExtract({ source: res.data.source, ...(res.data.extrait || {}) });
    } catch (err) {
      setExtractErr(err.response?.data?.error || "Extraction impossible.");
      setTimeout(() => setExtractErr(null), 6000);
    }
  };

  const applyExtract = async () => {
    if (!extract || !data) return;
    setApplying(true);
    const keep = (cur, ext) => (ext != null && ext !== '' ? ext : (cur ?? null));
    const p1 = data.parents?.find(p => p.type === 'parent1') || {};
    const p2 = data.parents?.find(p => p.type === 'parent2') || {};
    const mkParent = (cur, ext) => (ext && (ext.nom || ext.prenom || ext.telephone_portable || ext.email) ? {
      nom: keep(cur.nom, ext.nom), prenom: keep(cur.prenom, ext.prenom), lien: keep(cur.lien, ext.lien),
      telephone_portable: keep(cur.telephone_portable, ext.telephone_portable), telephone_pro: cur.telephone_pro ?? null,
      email: keep(cur.email, ext.email), adresse: cur.adresse ?? null, profession: cur.profession ?? null, situation_familiale: cur.situation_familiale ?? null,
    } : undefined);
    const body = {
      enfant: {
        structure_id: data.structure_id,
        nom: keep(data.nom, extract.enfant?.nom), prenom: keep(data.prenom, extract.enfant?.prenom),
        date_naissance: keep(data.date_naissance, extract.enfant?.date_naissance), sexe: keep(data.sexe, extract.enfant?.sexe),
        adresse: keep(data.adresse, extract.enfant?.adresse),
        medecin_nom: keep(data.medecin_nom, extract.medecin?.nom), medecin_tel: keep(data.medecin_tel, extract.medecin?.tel),
        allergies: data.allergies ?? null, antecedents: data.antecedents ?? null, pai: data.pai ? 1 : 0,
        statut: data.statut, date_entree_prevue: keep(data.date_entree_prevue, extract.contrat?.date_debut),
      },
      parent1: mkParent(p1, extract.parent1),
      parent2: mkParent(p2, extract.parent2),
    };
    try {
      await enfantsApi.update(id, body);
      await reload();
      setExtract(null);
    } catch (err) {
      setExtractErr(err.response?.data?.error || "Application impossible.");
    }
    setApplying(false);
  };

  const formatDate = (d) => {
    if (!d) return '—';
    try { return format(parseISO(d), 'd MMMM yyyy', { locale: fr }); }
    catch { return d; }
  };

  if (loading) return <div className="py-16 text-center text-gray-400">Chargement...</div>;
  if (!data) return <div className="py-16 text-center text-gray-400">Enfant non trouvé</div>;

  const parent1 = data.parents?.find(p => p.type === 'parent1');
  const parent2 = data.parents?.find(p => p.type === 'parent2');
  // E-mail famille robuste : 1er parent disposant d'une adresse (parent 1 ou 2)
  const emailFamille = [parent1, parent2].map(p => p?.email).find(e => e && e.includes('@'))
    || (data.parents || []).map(p => p.email).find(e => e && e.includes('@')) || '';
  const contratActif = data.contrats?.find(c => c.statut === 'actif');
  // Compte de crèche (tablette) : uniquement le quotidien (santé, parents, développement,
  // planning en lecture, documents de santé) — ni contrat, ni devis, ni export.
  const creche = estCompteCreche(user);
  // Équipe et tablette : aucune donnée financière (devis, contrat chiffré, n° CAF).
  const sansMontants = creche || user?.role === 'equipe';

  return (
    <div className="space-y-5 max-w-4xl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/enfants')} className="btn-secondary">
            <ChevronLeft size={16} /> Retour
          </button>
          <h2 className="text-xl font-bold text-gray-900">
            {data.prenom} {data.nom}
          </h2>
          <span className={getStatutBadgeClass(data.statut)}>{getStatutLabel(data.statut)}</span>
        </div>
        <div className="flex items-center gap-2">
          {creche ? (
            <Link to="/transmissions" className="btn-secondary">
              <ClipboardList size={15} /> Transmissions
            </Link>
          ) : <ExportDossier enfantId={data.id} />}
          {['admin', 'direction'].includes(user?.role) && (
            <>
              <Link to={`/devis/nouveau?enfant_id=${data.id}`} className="btn-secondary">
                <FileText size={15} /> Nouveau devis
              </Link>
              <Link to={`/enfants/${data.id}/modifier`} className="btn-primary">
                <Pencil size={15} /> Modifier
              </Link>
              <button
                onClick={async () => {
                  const archiver = !data.archive;
                  if (!window.confirm(archiver
                    ? `Archiver ${data.prenom} ${data.nom} ? L'enfant sera retiré de la liste active (aucune donnée supprimée).`
                    : `Réactiver ${data.prenom} ${data.nom} ?`)) return;
                  try {
                    await enfantsApi.setArchive(data.id, archiver);
                    if (archiver) navigate('/enfants');
                    else setData(d => ({ ...d, archive: 0 }));
                  } catch { window.alert('Action impossible.'); }
                }}
                className={data.archive ? 'btn-secondary' : 'btn-secondary text-amber-700'}
                title={data.archive ? 'Réactiver' : 'Archiver'}
              >
                {data.archive ? <ArchiveRestore size={15} /> : <Archive size={15} />} {data.archive ? 'Réactiver' : 'Archiver'}
              </button>
              <button
                onClick={() => { setDelText(''); setDelOpen(true); }}
                className="btn-secondary text-red-600 hover:bg-red-50"
                title="Supprimer définitivement"
              >
                <Trash2 size={15} /> Supprimer
              </button>
            </>
          )}
        </div>
      </div>

      {/* Informations enfant */}
      <Collapsible title="Informations enfant" icon={Baby} defaultOpen>
        <div className="space-y-2">
          <InfoRow label="Nom complet" value={`${data.prenom} ${data.nom}`} />
          <InfoRow label="Date de naissance" value={formatDate(data.date_naissance)} />
          <InfoRow label="Sexe" value={data.sexe === 'M' ? 'Masculin' : data.sexe === 'F' ? 'Féminin' : null} />
          <InfoRow label="Structure" value={data.structure_nom} />
          <InfoRow label="Entrée prévue" value={formatDate(data.date_entree_prevue)} />
          <InfoRow label="Adresse" value={data.adresse} />
          {!sansMontants && <InfoRow label="N° allocataire CAF" value={data.numero_allocataire} />}
          {data.medecin_nom && (
            <>
              <InfoRow label="Médecin" value={data.medecin_nom} />
              <InfoRow label="Tél médecin" value={data.medecin_tel} />
            </>
          )}
        </div>
        {(data.pai || data.allergies || data.antecedents) && (
          <div className="mt-4 pt-4 border-t border-gray-100 space-y-2">
            {data.pai ? (
              <div className="flex items-center gap-2 text-orange-600 text-sm font-medium">
                <AlertTriangle size={15} /> PAI actif
              </div>
            ) : null}
            {data.allergies && (
              <div className="text-sm">
                <span className="font-medium text-gray-600">Allergies : </span>
                <span className="text-gray-800">{data.allergies}</span>
              </div>
            )}
            {data.antecedents && (
              <div className="text-sm">
                <span className="font-medium text-gray-600">Antécédents : </span>
                <span className="text-gray-800">{data.antecedents}</span>
              </div>
            )}
          </div>
        )}
      </Collapsible>

      {/* Parents */}
      {[parent1, parent2].map((parent, i) => parent && (
        <Collapsible key={i} title={parent.lien || `Parent ${i + 1}`} icon={User}>
          <div className="space-y-2">
            <InfoRow label="Nom" value={`${parent.prenom || ''} ${parent.nom || ''}`.trim()} />
            <InfoRow label="Situation" value={parent.situation_familiale} />
            <InfoRow label="Profession" value={parent.profession} />
          </div>
          <div className="mt-3 pt-3 border-t border-gray-100 flex flex-col gap-1.5">
            {parent.telephone_portable && (
              <a href={`tel:${parent.telephone_portable}`} className="flex items-center gap-2 text-sm text-gray-700 hover:text-primary-500">
                <Phone size={14} className="text-gray-400" /> {parent.telephone_portable}
              </a>
            )}
            {parent.telephone_pro && (
              <a href={`tel:${parent.telephone_pro}`} className="flex items-center gap-2 text-sm text-gray-700 hover:text-primary-500">
                <Phone size={14} className="text-gray-400" /> {parent.telephone_pro} (pro)
              </a>
            )}
            {parent.email && (
              <a href={`mailto:${parent.email}`} className="flex items-center gap-2 text-sm text-gray-700 hover:text-primary-500">
                <Mail size={14} className="text-gray-400" /> {parent.email}
              </a>
            )}
          </div>
        </Collapsible>
      ))}

      {/* Suivi du développement */}
      <Collapsible title="Suivi du développement" icon={Sparkles} flatten>
        <SuiviDeveloppement enfantId={data.id} enfantPrenom={data.prenom} />
      </Collapsible>

      {/* Documents & Autorisations (générés à la charte, pré-remplis) */}
      {!creche && (
        <Collapsible title="Documents & Autorisations" icon={FileSignature} flatten>
          <DocumentsAutorisations enfantId={data.id} />
        </Collapsible>
      )}

      {/* Contrat d'accueil : 1er contrat pré-rempli, puis AVENANT si un contrat existe déjà */}
      {['admin', 'direction'].includes(user?.role) && (
        (data.contrats || []).length === 0 ? (
          <Collapsible title="Contrat d'accueil" icon={FileSignature}>
            <ContratAccueilCard enfantId={data.id} onChange={reload} />
            <div className="flex items-center justify-between flex-wrap gap-3 mt-4 pt-4 border-t border-gray-100">
              <p className="text-xs text-gray-400">Génère le contrat d'accueil <strong>pré-rempli</strong> (enfant, parents, jours &amp; horaires, volume, tarif, dates — tout ce qui est connu dans la fiche/préinscription), seuls les champs à signer par les parents restent vierges. Télécharge-le en PDF ou envoie-le en signature (2 parents + la crèche).</p>
              <div className="flex items-center gap-2 flex-shrink-0">
                <a href={enfantsApi.getContratPdfUrl(data.id)} target="_blank" rel="noopener noreferrer" className="btn-primary"><FileText size={15} /> Générer le contrat d'accueil</a>
                <EnvoiDocumentButton type="contrat_accueil" enfantId={data.id} refId={data.id} destinataireDefaut={emailFamille} label="Envoyer" />
                <SignatureButton type="contrat_accueil" refId={data.id} email={emailFamille} nom={`${parent1?.prenom || ''} ${parent1?.nom || ''}`.trim()} />
              </div>
            </div>
          </Collapsible>
        ) : (
          <Collapsible title="Contrat d'accueil" icon={FileSignature} badge={contratActif && <span className="badge-green">Actif</span>}>
            <ContratAccueilCard enfantId={data.id} onChange={reload} />
            <div className="flex items-start justify-between flex-wrap gap-3 mt-4 pt-4 border-t border-gray-100">
              <p className="text-xs text-gray-400">Un contrat d'accueil <strong>existe déjà</strong> pour cet enfant. Toute modification (planning, tarif, dates…) se fait par <strong>avenant</strong>. L'avenant reprend les <strong>nouvelles conditions</strong> pré-remplies et renvoie au contrat initial.
                <br /><a href={enfantsApi.getContratPdfUrl(data.id)} target="_blank" rel="noopener noreferrer" className="text-navy-600 underline">Revoir le contrat d'accueil initial</a></p>
              <div className="flex items-center gap-2 flex-shrink-0">
                <a href={enfantsApi.getAvenantPdfUrl(data.id)} target="_blank" rel="noopener noreferrer" className="btn-primary"><FileText size={15} /> Générer un avenant</a>
                <EnvoiDocumentButton type="avenant" enfantId={data.id} refId={data.id} destinataireDefaut={emailFamille} label="Envoyer" />
              </div>
            </div>
          </Collapsible>
        )
      )}

      {/* Envoi de la convention de réservation à signer — le document signé revient dans la bibliothèque */}
      {['admin', 'direction'].includes(user?.role) && (
        <Collapsible title="Convention de réservation" icon={FileSignature}>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <p className="text-xs text-gray-400">Envoyer à signer par voie électronique. Une fois signée, elle se dépose automatiquement dans la bibliothèque de l'enfant.</p>
            <div className="flex items-center gap-2 flex-shrink-0">
              <EnvoiDocumentButton type="convention" enfantId={data.id} refId={data.id} destinataireDefaut={parent1?.email} label="Envoyer" />
              <SignatureButton type="convention" refId={data.id} email={parent1?.email} nom={`${parent1?.prenom || ''} ${parent1?.nom || ''}`.trim()} />
            </div>
          </div>
        </Collapsible>
      )}

      {/* Mandat de prélèvement SEPA (coordonnées bancaires : admin/direction) */}
      {['admin', 'direction'].includes(user?.role) && (
        <Collapsible title="Mandat de prélèvement SEPA" icon={FileSignature}>
          <MandatSepaCard enfantId={data.id} emailFamille={emailFamille} />
        </Collapsible>
      )}

      {/* Planning enfant + Attestation mensuelle (admin/direction) */}
      {['admin', 'direction'].includes(user?.role) && (
        <>
          <Collapsible title="Planning de l'enfant" icon={CalendarCheck} flatten>
            <PlanningCard enfantId={data.id} emailFamille={emailFamille} nomFamille={`${parent1?.prenom || ''} ${parent1?.nom || ''}`.trim()} />
          </Collapsible>
          <Collapsible title="Attestation de présence mensuelle" icon={CalendarCheck} flatten>
            <AttestationCard enfantId={data.id} destinataireEmail={parent1?.email} onDeposee={() => setBiblioKey(k => k + 1)} />
          </Collapsible>
        </>
      )}

      {/* Compte de crèche : planning de l'enfant en consultation seule */}
      {creche && (
        <Collapsible title="Planning de l'enfant" icon={CalendarCheck} flatten>
          <PlanningCard enfantId={data.id} readOnly />
        </Collapsible>
      )}

      {/* Pièces jointes libres (Word, PDF, Excel, tableur, image…) — avec extraction IA */}
      {extractErr && (
        <div className="text-[13px] text-red-600 bg-red-50 border border-red-100 rounded-xl px-4 py-2.5">{extractErr}</div>
      )}
      <Collapsible title="Bibliothèque de documents" icon={FileText} defaultOpen flatten>
        <PiecesJointes key={biblioKey} entiteType="enfant" entiteId={data.id} title="" onExtract={creche ? null : handleExtract} library />
      </Collapsible>

      {extract && (
        <ExtractModal extract={extract} applying={applying} onCancel={() => setExtract(null)} onApply={applyExtract} />
      )}

      {/* Suppression définitive — garde-fou : saisir « SUPPRIMER » */}
      {delOpen && (
        <div className="fixed inset-0 bg-navy-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => !deleting && setDelOpen(false)}>
          <div className="bg-white rounded-[22px] shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="px-6 pt-5 pb-3 flex items-center gap-2">
              <div className="w-9 h-9 rounded-full bg-red-50 flex items-center justify-center text-red-600"><Trash2 size={18} /></div>
              <h3 className="font-serif text-[20px] text-navy-900">Supprimer définitivement</h3>
            </div>
            <div className="px-6 pb-6 space-y-3">
              <p className="text-[13.5px] text-gray-600">
                Vous êtes sur le point de supprimer <strong>définitivement</strong> le dossier de <strong>{data.prenom} {data.nom}</strong> :
                ses informations, parents, pièces jointes, planning et échéances. <span className="text-red-600 font-medium">Cette action est irréversible.</span>
              </p>
              <p className="text-[12.5px] text-emerald-700 bg-emerald-50 rounded-lg px-3 py-2">
                📦 Un <strong>export complet du dossier</strong> (fiche + dossier PDF + toutes les pièces) sera <strong>téléchargé automatiquement</strong> avant la suppression.
              </p>
              <p className="text-[12.5px] text-gray-500">Pour confirmer, écrivez <strong>SUPPRIMER</strong> ci-dessous.</p>
              <input autoFocus value={delText} onChange={e => setDelText(e.target.value)} placeholder="SUPPRIMER"
                className="form-input w-full uppercase tracking-wide" />
              <div className="flex justify-end gap-2 pt-1">
                <button onClick={() => setDelOpen(false)} disabled={deleting} className="btn-secondary">Annuler</button>
                <button
                  onClick={async () => {
                    if (delText.trim().toUpperCase() !== 'SUPPRIMER') return;
                    setDeleting(true);
                    try {
                      const { data: r } = await enfantsApi.delete(data.id);
                      // Télécharge automatiquement l'archive complète du dossier avant de quitter
                      if (r?.archive) { try { window.open(enfantsApi.archiveDossierUrl(r.archive), '_blank'); } catch { /* popup bloquée */ } }
                      navigate('/enfants');
                    }
                    catch (e) { window.alert(e.response?.data?.error || 'Suppression impossible'); setDeleting(false); }
                  }}
                  disabled={deleting || delText.trim().toUpperCase() !== 'SUPPRIMER'}
                  className="inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {deleting ? <Loader size={15} className="animate-spin" /> : <Trash2 size={15} />} Supprimer définitivement
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Devis de l'enfant — toujours visible depuis la fiche */}
      {!sansMontants && (() => {
        const devisList = data.devis || [];
        const supprimerDevis = async (did) => {
          if (!window.confirm(`Supprimer le devis #${did} ? Cette action est définitive.`)) return;
          try { await devisApi.delete(did); reload(); }
          catch (err) { alert(err.response?.data?.error || 'Suppression impossible (devis converti en contrat ?).'); }
        };
        return (
          <Collapsible title={`Devis (${devisList.length})`} icon={FileText}
            badge={<Link to={`/devis/nouveau?enfant_id=${data.id}`} onClick={e => e.stopPropagation()} className="text-xs text-primary-500 hover:underline">+ Nouveau devis</Link>}>
            {devisList.length === 0 ? (
              <div className="text-center py-6">
                <div className="text-sm text-gray-400 mb-3">Aucun devis pour cet enfant.</div>
                <Link to={`/devis/nouveau?enfant_id=${data.id}`} className="btn-primary inline-flex">
                  <FileText size={15} /> Créer un devis
                </Link>
              </div>
            ) : (
              <div className="space-y-2">
                {devisList.map(d => (
                  <div key={d.id} className="flex items-center justify-between gap-2 p-3 rounded-lg bg-gray-50">
                    <Link to={`/devis/${d.id}`} className="min-w-0 hover:underline">
                      <div className="text-sm font-medium text-gray-800">Devis {d.numero ? d.numero : `#${d.id}`}</div>
                      <div className="text-xs text-gray-500">
                        {d.date_debut_contrat ? `Début : ${formatDate(d.date_debut_contrat)}` : d.date_devis}
                        {' · '}{d.heures_hebdo}h/sem
                      </div>
                    </Link>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-sm font-semibold">{formatMontant(d.montant_mensuel)}/mois</span>
                      <span className={getStatutBadgeClass(d.statut)}>{getStatutLabel(d.statut)}</span>
                      <a href={devisApi.getPdfUrl(d.id)} target="_blank" rel="noreferrer"
                        title="Ouvrir le PDF du devis"
                        className="inline-flex items-center justify-center w-8 h-8 rounded-full hover:bg-navy-50 text-navy-400 hover:text-navy-700">
                        <Download size={15} />
                      </a>
                      <EnvoiDocumentButton compact type="devis" refId={d.id} enfantId={data.id} destinataireDefaut={emailFamille} />
                      <button type="button" onClick={() => supprimerDevis(d.id)} title="Supprimer le devis"
                        className="inline-flex items-center justify-center w-8 h-8 rounded-full hover:bg-red-50 text-gray-400 hover:text-red-600">
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Collapsible>
        );
      })()}

      {/* Suivi des e-mails avec la famille (réservé à la gestion) */}
      {['admin', 'direction'].includes(user?.role) && (
        <Collapsible title="Suivi des e-mails" icon={Mail} flatten>
          <SuiviMails enfantId={data.id} />
        </Collapsible>
      )}
    </div>
  );
}
